// ./src/hooks/useUsers.ts
//
// Data hook for User CRUD operations.
//
// Follows the same architecture as useExpenses:
// - OpResult return type on all operations for consistent error handling
// - Race-condition protection via monotonic listCallIdRef
// - currentPageRef to avoid stale closures in loadMore
// - Mount-time refs for pageSize, initialFilters, initialSort
// - lastQueriedFiltersRef / lastQueriedSortRef for loadMore/refresh
// - ApiError integration (not plain strings)
// - isLoaded tracking
// - Memoized return object

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import {
  User,
  UserInput,
  UserFilter,
  UserUpdateManyInput,
  UserService,
  Pagination,
  SortItem,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError } from "@/utils";
import { DEFAULT_PAGE_SIZE } from "@/config";

// ============================================
// Types
// ============================================

export interface UseUsersOptions {
  /**
   * Page size for pagination.
   *
   * This is a **mount-time** configuration value. If you need to change
   * page size after mount, pass it directly to `list()` or `loadMore()`.
   */
  pageSize?: number;

  /**
   * Filters applied on initial autoLoad and userId-change reloads.
   *
   * This is a **mount-time** configuration value. Changing this prop
   * after mount has **no effect** — the hook does not auto-re-fetch.
   * To apply new filters, call `list(newFilters)` explicitly.
   */
  initialFilters?: Partial<UserFilter>;

  /**
   * Sort configuration applied on initial autoLoad and userId-change reloads.
   *
   * This is a **mount-time** configuration value. Changing this prop
   * after mount has **no effect** — the hook does not auto-re-fetch.
   * To apply a new sort, pass it directly to `list()`.
   *
   * @default [{ name: "username", order: "ASC" }]
   */
  initialSort?: SortItem[];

  /** Auto-load data on mount (and on userId change) */
  autoLoad?: boolean;
}

export interface UseUsersReturn {
  // Data state
  user: User | null;
  users: User[];
  loading: boolean;
  isLoaded: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  isEmpty: boolean;

  // Search and filters
  filters: Partial<UserFilter>;
  sortBy: SortItem[];

  // Pagination
  currentPage: number;
  pageSize: number;

  // Operations state
  isCreating: boolean;
  isUpdating: boolean;
  removingId: string | null;
  selectedUser: User | null;

  // Core operations (matching API method names exactly)
  get: (id: string) => Promise<OpResult>;
  list: (
    filter?: Partial<UserFilter>,
    pagination?: Pagination,
    sortBy?: SortItem[],
    shouldReset?: boolean
  ) => Promise<OpResult>;
  getMany: (ids: string[]) => Promise<OpResult>;
  create: (params: UserInput) => Promise<OpResult>;
  createMany: (params: UserInput[]) => Promise<OpResult>;
  update: (id: string, params: UserInput) => Promise<OpResult>;
  updateMany: (params: UserUpdateManyInput[]) => Promise<OpResult>;
  remove: (id: string) => Promise<OpResult>;
  removeMany: (ids: string[]) => Promise<OpResult>;

  // Additional convenience methods
  loadMore: () => Promise<void>;
  refresh: () => Promise<OpResult>;

  // UI state management
  setSelectedUser: (user: User | null) => void;

  // Helper operations
  reset: () => void;

  // Computed values (for selected/current user)
  fullName: string | null;
  displayName: string | null;
  isEmailConfirmed: boolean;
  hasToken: boolean;
  usersByStatus: Record<string, User[]>;

  // Lookup helpers
  getUserById: (id: string | undefined) => User | undefined;

  // Error handling
  lastError: ApiError | null;
  clearError: () => void;
}

// ============================================
// Default Values
// ============================================

const DEFAULT_SORT: SortItem[] = [{ name: "username", order: "ASC" }];
const DEFAULT_FILTERS: Partial<UserFilter> = {};

// ============================================
// Hook Implementation
// ============================================

export const useUsers = (options: UseUsersOptions = {}): UseUsersReturn => {
  const {
    pageSize = DEFAULT_PAGE_SIZE,
    initialFilters = DEFAULT_FILTERS,
    initialSort = DEFAULT_SORT,
    autoLoad = false,
  } = options;

  // Get user from AppContext
  const { user: appUser } = useApp();
  const userId = appUser?.id ?? null;

  // ── Mount-time configuration captured in refs ──
  // These are read-only after mount. They're used by:
  //   - The userId-change effect (for autoLoad re-fetches)
  //   - loadMore / refresh (pageSize and sort)
  // We use refs (not state) so `list()` and other callbacks don't
  // have stale closures, and so effects don't re-fire on every render.
  const pageSizeRef = useRef(pageSize);
  const initialFiltersRef = useRef(initialFilters);
  const initialSortRef = useRef(initialSort);

  // Ref to track the last-queried filters for loadMore
  // so we never read stale `filters` state from a closure.
  const lastQueriedFiltersRef = useRef<Partial<UserFilter>>(initialFilters);

  // Ref to track the last-queried sort for loadMore/refresh
  const lastQueriedSortRef = useRef<SortItem[]>(initialSort);

  // Monotonically increasing call ID to detect stale list() responses.
  const listCallIdRef = useRef(0);

  // Track previous userId for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Ref to track current page for loadMore — avoids stale closures
  // when multiple rapid loadMore calls happen before state settles.
  const currentPageRef = useRef(1);

  // Data state
  const [user, setUser] = useState<User | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Search and filters — tracks what was last queried (for display/debugging)
  const [filters, setFilters] = useState<Partial<UserFilter>>(initialFilters);
  const [sortBy, setSortBy] = useState<SortItem[]>(initialSort);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  // Operations state
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Error handling
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && users.length === 0,
    [isLoaded, users.length]
  );

  const fullName = useMemo(() => {
    const currentUser = selectedUser || user;
    if (!currentUser) return null;
    const parts = [
      currentUser.firstName,
      currentUser.middleName,
      currentUser.lastName,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" ") : null;
  }, [
    selectedUser?.firstName,
    selectedUser?.middleName,
    selectedUser?.lastName,
    user?.firstName,
    user?.middleName,
    user?.lastName,
    selectedUser,
    user,
  ]);

  const displayName = useMemo(() => {
    const currentUser = selectedUser || user;
    if (!currentUser) return null;
    return (
      fullName || currentUser.username || currentUser.email || "Unknown User"
    );
  }, [
    fullName,
    selectedUser?.username,
    selectedUser?.email,
    user?.username,
    user?.email,
    selectedUser,
    user,
  ]);

  const isEmailConfirmed = useMemo(() => {
    const currentUser = selectedUser || user;
    return currentUser?.emailConfirmation ?? false;
  }, [selectedUser?.emailConfirmation, user?.emailConfirmation, selectedUser, user]);

  const hasToken = useMemo(() => {
    const currentUser = selectedUser || user;
    return Boolean(currentUser?.token);
  }, [selectedUser?.token, user?.token, selectedUser, user]);

  const usersByStatus = useMemo(() => {
    return users.reduce(
      (acc, u) => {
        const status = String(u.status || "unknown");
        if (!acc[status]) {
          acc[status] = [];
        }
        acc[status].push(u);
        return acc;
      },
      {} as Record<string, User[]>
    );
  }, [users]);

  // ==========================================
  // List Helpers
  // ==========================================

  // Helper function to update item in list if it exists, or add it if not
  const updateOrAddInList = useCallback((updatedItem: User) => {
    setUsers((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.id === updatedItem.id
      );
      if (existingIndex >= 0) {
        const newItems = [...prev];
        newItems[existingIndex] = updatedItem;
        return newItems;
      } else {
        return [updatedItem, ...prev];
      }
    });
  }, []);

  // Helper function to remove item from list if it exists
  const removeFromList = useCallback((id: string) => {
    setUsers((prev) => prev.filter((item) => item.id !== id));
  }, []);

  // Helper function to remove multiple items from list
  const removeManyFromList = useCallback((ids: string[]) => {
    const idsSet = new Set(ids);
    setUsers((prev) => prev.filter((item) => !idsSet.has(item.id || "")));
  }, []);

  // Clear error function
  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // Reset function
  const reset = useCallback(() => {
    // Invalidate any in-flight list() calls so their responses are ignored
    listCallIdRef.current++;

    setUser(null);
    setUsers([]);
    setLastError(null);
    setLoading(false);
    setIsLoaded(false);
    setLoadingMore(false);
    setHasMore(false);
    setCurrentPage(1);
    currentPageRef.current = 1;
    setIsCreating(false);
    setIsUpdating(false);
    setRemovingId(null);
    setSelectedUser(null);
  }, []);

  // ==========================================
  // Core Operations
  // ==========================================

  // Get single user
  const get = useCallback(
    async (id: string): Promise<OpResult> => {
      setLoading(true);
      setLastError(null);

      try {
        const result = await UserService.get(id);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const data = (result.data as User[])[0];
          setUser(data);
          updateOrAddInList(data);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load user"));
        setUser(null);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load user"));
        setUser(null);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  /**
   * List users.
   * - shouldReset=true: replaces users array (page 1 load / filter change)
   * - shouldReset=false: appends to existing users (load more)
   *
   * Race-condition safe: each call gets a monotonic ID. If a newer call has
   * started by the time the response arrives, the stale response is discarded.
   *
   * Note: This callback has an empty dependency array because it only accesses
   * external values via refs (pageSizeRef, lastQueriedFiltersRef, etc.) and
   * React state setters (setLoading, setUsers, etc.) whose identity is
   * guaranteed stable by React across renders.
   */
  const list = useCallback(
    async (
      filter: Partial<UserFilter> = {},
      pagination?: Pagination,
      sortByParams?: SortItem[],
      shouldReset: boolean = true
    ): Promise<OpResult> => {
      const callId = ++listCallIdRef.current;
      const targetPage = pagination?.page || 1;
      const targetPageSize = pagination?.pageSize || pageSizeRef.current;
      const isAppending = !shouldReset;

      if (isAppending) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        // Don't clear users here — keep stale data visible while loading.
        // The array is replaced atomically when the response arrives.
        setCurrentPage(1);
        currentPageRef.current = 1;
      }

      setLastError(null);

      // Store the queried filters/sort so loadMore and refresh can use
      // them via refs — avoids stale closures.
      setFilters(filter);
      lastQueriedFiltersRef.current = filter;
      if (sortByParams) {
        setSortBy(sortByParams);
        lastQueriedSortRef.current = sortByParams;
      }

      const currentSortBy = sortByParams || lastQueriedSortRef.current;

      try {
        const paginationParams: Pagination = {
          page: targetPage,
          pageSize: targetPageSize,
        };

        const sortParams = currentSortBy.map((sort) => sort);

        const result = await UserService.list(
          filter as UserFilter,
          paginationParams,
          sortParams
        );

        // Guard: if a newer list() call was initiated while we were waiting,
        // discard this response to avoid overwriting fresher data.
        if (callId !== listCallIdRef.current) {
          return result;
        }

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as User[])
            : [];

          if (isAppending) {
            setUsers((prev) => {
              const existingIds = new Set(prev.map((item) => item.id));
              const uniqueNewItems = newItems.filter(
                (item) => !existingIds.has(item.id)
              );
              return [...prev, ...uniqueNewItems];
            });
          } else {
            setUsers(newItems);
          }

          setHasMore(newItems.length === targetPageSize);
          setCurrentPage(targetPage);
          currentPageRef.current = targetPage;
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load users"));
        setIsLoaded(true);
        if (!isAppending) setUsers([]);
        return result;
      } catch (error) {
        // Guard stale responses for error path too
        if (callId !== listCallIdRef.current) {
          return OpResult.fromException(error);
        }

        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load users"));
        setIsLoaded(true);
        if (!isAppending) setUsers([]);
        return result;
      } finally {
        // Only update loading flags if this call is still the latest.
        if (callId === listCallIdRef.current) {
          if (isAppending) {
            setLoadingMore(false);
          } else {
            setLoading(false);
          }
        }
      }
    },
    []
  );

  // Get many users by IDs
  const getMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        return new OpResult();
      }

      setLoading(true);
      setLastError(null);

      try {
        const result = await UserService.getMany(ids);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const loadedItems = Array.isArray(result.data)
            ? (result.data as User[])
            : [];
          loadedItems.forEach((item) => {
            updateOrAddInList(item);
          });
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to load users"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to load users"));
        return result;
      } finally {
        setLoading(false);
      }
    },
    [updateOrAddInList]
  );

  // ==========================================
  // Mutation Operations
  // ==========================================

  // Create user
  const create = useCallback(
    async (params: UserInput): Promise<OpResult> => {
      setIsCreating(true);
      setLastError(null);

      try {
        const result = await UserService.create(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItem = (result.data as User[])[0];
          setUser(newItem);
          setUsers((prev) => [newItem, ...prev]);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create user"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create user"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Create many users
  const createMany = useCallback(
    async (params: UserInput[]): Promise<OpResult> => {
      if (params.length === 0) {
        return new OpResult();
      }

      setIsCreating(true);
      setLastError(null);

      try {
        const result = await UserService.createMany(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const newItems = Array.isArray(result.data)
            ? (result.data as User[])
            : [];
          setUsers((prev) => [...newItems, ...prev]);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to create users"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to create users"));
        return result;
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  // Update user
  const update = useCallback(
    async (id: string, params: UserInput): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "User ID is required for updating"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await UserService.update({ id }, params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItem = (result.data as User[])[0];
          setUser(updatedItem);
          updateOrAddInList(updatedItem);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update user"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update user"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    [updateOrAddInList]
  );

  // Update many users
  const updateMany = useCallback(
    async (params: UserUpdateManyInput[]): Promise<OpResult> => {
      if (params.length === 0) {
        return new OpResult();
      }

      setIsUpdating(true);
      setLastError(null);

      try {
        const result = await UserService.updateMany(params);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const updatedItems = Array.isArray(result.data)
            ? (result.data as User[])
            : [];

          // Update users in the list
          setUsers((prev) =>
            prev.map((existingUser) => {
              const updated = updatedItems.find(
                (u) => u.id === existingUser.id
              );
              return updated || existingUser;
            })
          );

          // Update single user if it matches any of the updated users
          setUser((prev) => {
            if (!prev) return prev;
            const updatedSingleUser = updatedItems.find(
              (u) => u.id === prev.id
            );
            return updatedSingleUser || prev;
          });

          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to update users"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to update users"));
        return result;
      } finally {
        setIsUpdating(false);
      }
    },
    []
  );

  // Remove user
  const remove = useCallback(
    async (id: string): Promise<OpResult> => {
      if (!id) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "User ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setRemovingId(id);
      setLastError(null);

      try {
        const result = await UserService.remove({ id });

        if (result.code === OP_RESULT_CODES.OK) {
          setUser(null);
          removeFromList(id);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove user"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove user"));
        return result;
      } finally {
        setRemovingId(null);
      }
    },
    [removeFromList]
  );

  // Remove many users
  const removeMany = useCallback(
    async (ids: string[]): Promise<OpResult> => {
      if (ids.length === 0) {
        const result = OpResult.fail(
          OP_RESULT_CODES.VALIDATION_FAILED,
          {},
          "At least one User ID is required for removal"
        );
        setLastError(ApiError.fromResult(result));
        return result;
      }

      setLastError(null);

      const userWhereInputs = ids.map((id) => ({ id }));

      try {
        const result = await UserService.removeMany(userWhereInputs);

        if (result.code === OP_RESULT_CODES.OK) {
          setUser(null);
          removeManyFromList(ids);
          setLastError(null);
          return result;
        }

        setLastError(ApiError.fromResult(result, "Failed to remove users"));
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(ApiError.fromException(error, "Failed to remove users"));
        return result;
      }
    },
    [removeManyFromList]
  );

  // ==========================================
  // Convenience Methods
  // ==========================================

  // Load more users
  // Uses refs for filters, sort, and current page to avoid stale closures.
  // Reading currentPage from a ref (instead of state) ensures that rapid
  // successive loadMore calls always compute the correct next page, even
  // if React hasn't flushed the state update from the previous call yet.
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;

    const nextPage = currentPageRef.current + 1;
    await list(
      lastQueriedFiltersRef.current,
      { page: nextPage, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      false
    );
  }, [list, loadingMore, hasMore]);

  // Refresh users (reset to page 1)
  // Uses refs so it always picks up the last-queried filters/sort.
  const refresh = useCallback(async (): Promise<OpResult> => {
    return list(
      lastQueriedFiltersRef.current,
      { page: 1, pageSize: pageSizeRef.current },
      lastQueriedSortRef.current,
      true
    );
  }, [list]);

  // ==========================================
  // Derived Data Helpers
  // ==========================================

  // Lookup helper
  const getUserById = useCallback(
    (id: string | undefined): User | undefined => {
      if (!id) return undefined;
      return users.find((item) => item.id === id);
    },
    [users]
  );

  // ==========================================
  // Effects
  // ==========================================

  // Handle user change - reset and reload data when userId changes
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        list(
          initialFiltersRef.current,
          { page: 1, pageSize: pageSizeRef.current },
          initialSortRef.current,
          true
        );
      }
    }
  }, [userId, autoLoad, list, reset]);

  // ==========================================
  // Return
  // ==========================================

  return useMemo(
    () => ({
      // Data state
      user,
      users,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,

      // Search and filters
      filters,
      sortBy,

      // Pagination
      currentPage,
      pageSize: pageSizeRef.current,

      // Operations state
      isCreating,
      isUpdating,
      removingId,
      selectedUser,

      // Core operations
      get,
      list,
      getMany,
      create,
      createMany,
      update,
      updateMany,
      remove,
      removeMany,

      // Additional convenience methods
      loadMore,
      refresh,

      // UI state management
      setSelectedUser,

      // Helper operations
      reset,

      // Computed values
      fullName,
      displayName,
      isEmailConfirmed,
      hasToken,
      usersByStatus,

      // Lookup helpers
      getUserById,

      // Error handling
      lastError,
      clearError,
    }),
    [
      user,
      users,
      loading,
      isLoaded,
      loadingMore,
      hasMore,
      isEmpty,
      filters,
      sortBy,
      currentPage,
      isCreating,
      isUpdating,
      removingId,
      selectedUser,
      get,
      list,
      getMany,
      create,
      createMany,
      update,
      updateMany,
      remove,
      removeMany,
      loadMore,
      refresh,
      reset,
      fullName,
      displayName,
      isEmailConfirmed,
      hasToken,
      usersByStatus,
      getUserById,
      lastError,
      clearError,
    ]
  );
};